import {ActionTree} from 'vuex'
import axios, {AxiosResponse, AxiosError} from "axios";
import * as moment from 'moment';
import Category from '../ts/CategoryInterface';
import ProjectStatsInterface from "../ts/ProjectStatsInterface";
import SortConfig from "../ts/SortConfig";
import ProjectGeoStatsDataInterface from "../ts/ProjectGeoStatsDataInterface";
import ProjectErrorInterface from "../ts/ProjectErrorInterface";

export const actions: ActionTree<any, any> = {

    loadProjects: ({state, getters, commit, dispatch}, {force}) => {

        if (force) {
            state.projectsCacheExpires = null;
        }

        return new Promise((resolve, reject) => {

            // Pull from store if loaded and cache hasn't expired
            if (state.projectsCacheExpires === null || moment().isAfter(state.projectsCacheExpires)) {

                commit('setProjectsLoading', true);

                let url = '/projects';

                axios.get(url)

                    .then(({data}) => {

                        commit('setProjectsLoading', false);
                        commit('setProjects', data.projects);
                        resolve(null);

                    })
                    .catch(error => {
                        commit('setProjectsLoading', false);
                        reject(error);
                    });
            } else {
                resolve(null);
            }
        });
    },

    loadArchivedProjects: ({state, getters, commit, dispatch}, config: SortConfig) => {

        return new Promise((resolve, reject) => {

            let url = '/projects/archived?page=' + config.page
                + '&sortBy=' + config.sortBy
                + '&itemsPerPage=' + config.itemsPerPage
                + '&sortDesc=' + config.sortDesc;

            if (config.search && config.search.length > 0) {
                url = url + '&search=' + config.search;
            }

            axios.get(url)

                .then(({data}) => {
                    commit('setArchivedProjects', data.projects.data);
                    resolve(data.projects);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    getProject: ({state, getters, commit, dispatch}, {id, force}) => {

        return new Promise((resolve, reject) => {

            // See if project is found already
            const project = getters.findProject(id);

            if (project && !force) {

                resolve(project);

            } else {

                axios.get('/project/' + id)

                    .then(({data}) => {
                        commit('updateProject', data.project)
                        resolve(data.project);
                    })
                    .catch(error => {
                        reject(error);
                    });
            }
        });
    },

    createProject: ({state, getters, commit, dispatch}, form) => {

        return new Promise((resolve, reject) => {

            form.send('post', '/project')

                .then((response: AxiosResponse) => {

                    commit('createProject', response.data.project);

                    resolve(response);
                })
                .catch((error: AxiosError) => {


                    reject(error);
                });

        });
    },

    updateProject: ({state, getters, commit, dispatch}, form) => {

        return new Promise((resolve, reject) => {

            form.send('put', '/project')

                .then((response: AxiosResponse) => {

                    commit('updateProject', response.data.project);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });

        });
    },

    changeProjectStatus: ({state, getters, commit, dispatch}, data) => {

        return new Promise((resolve, reject) => {

            const projectsBackup = [...state.projects];

            commit('updateProjectStatus', data);

            axios.put('/project/' + data.id + '/status', {status: data.status})

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    commit('setProjects', projectsBackup);
                    reject(error);
                });

        });
    },

    deleteProject: ({state, getters, commit, dispatch}, projectId: number) => {

        return new Promise((resolve, reject) => {

            axios.delete('/project/' + projectId)

                .then((response: AxiosResponse) => {

                    commit('deleteProject', projectId);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });

        });
    },

    cloneProject: ({state, getters, commit, dispatch}, projectId: number) => {

        return new Promise((resolve, reject) => {

            axios.get('/project/' + projectId + '/clone')

                .then((response: AxiosResponse) => {

                    commit('createProject', response.data.project);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });

        });
    },

    loadCategories: ({state, getters, commit, dispatch}, forceUpdate: boolean) => {

        if (forceUpdate) {
            state.categoriesCacheExpires = null;
        }

        // Pull from store if loaded and cache hasn't expired
        if (state.categoriesCacheExpires === null ||
            moment().isAfter(state.categoriesCacheExpires)) {

            commit('setCategoriesLoading', true);

            return new Promise((resolve, reject) => {

                axios.get('/categories')

                    .then(({data}) => {

                        commit('setCategories', data.categories);
                        commit('setCategoriesLoading', false);
                        resolve(null);
                    })
                    .catch(error => {
                        commit('setCategoriesLoading', false);
                        reject(error);
                    });
            });
        }
    },

    createCategory: ({state, getters, commit, dispatch}, category: Category) => {

        // Clone original
        const categories = [...state.categories];

        commit('createCategory', category);

        return new Promise((resolve, reject) => {

            axios.post('/category', category)

                .then((response: AxiosResponse) => {

                    commit('saveCategoryId', response.data.category.id);

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('setCategories', categories);

                    reject(error);
                });
        });
    },

    updateCategory: ({state, getters, commit, dispatch}, category: Category) => {

        // Clone original
        const categories = [...state.categories];

        commit('updateCategory', category);

        return new Promise((resolve, reject) => {

            axios.put('/category', category)

                .then((response: AxiosResponse) => {

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('setCategories', categories);

                    reject(error);
                });
        });
    },

    /**
     * Delete category
     */
    deleteCategory: ({state, getters, commit, dispatch}, category: Category) => {

        // Clone original
        const categories = [...state.categories];

        commit('deleteCategory', category);

        return new Promise((resolve, reject) => {

            axios.delete('/category/' + category.id)

                .then((response: AxiosResponse) => {

                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    // Restore original object
                    commit('setCategories', categories);

                    reject(error);
                });
        });
    },

    /**
     * Get project invitation / response statistics by time function
     */
    getProjectStats: ({state, getters, commit, dispatch}, {forceUpdate, projectId}) => {

        return new Promise((resolve, reject) => {

            let found = null;

            if (!forceUpdate) {
                found = state.projectStats.find((stats: ProjectStatsInterface) => {
                    return stats.id === projectId;
                });
            }

            // Pull from store if loaded and cache hasn't expired
            if (!found || found.expires === null || moment().isAfter(found.expires)) {

                commit('setProjectStatsLoading', true);

                axios.get('/project/' + projectId + '/stats')

                    .then(({data}) => {

                        const projectData = {
                            id: projectId,
                            data: data.items,
                            first_recorded_activity: data.first_recorded_activity,
                            last_recorded_activity: data.last_recorded_activity,
                            expires: moment().add(15, 'minutes'),
                        };

                        commit('setProjectStats', projectData);
                        commit('setProjectStatsLoading', false);
                        resolve(projectData);
                    })
                    .catch(error => {
                        commit('setProjectStatsLoading', false);
                        reject(error);
                    });
            } else {
                resolve(found);
            }
        });
    },

    /**
     * Get project invitation / response statistics by time function
     */
    getProjectGeoStats: ({state, getters, commit, dispatch}, {forceUpdate, projectId}) => {

        return new Promise((resolve, reject) => {

            let found = null;

            if (!forceUpdate) {
                found = state.projectGeoStats.find((stats: ProjectGeoStatsDataInterface) => {
                    return stats.id === projectId;
                });
            }

            // Pull from store if loaded and cache hasn't expired
            if (!found || moment().isAfter(found.expires)) {

                commit('setProjectGeoStatsLoading', true);

                axios.get('/project/' + projectId + '/geo-stats')

                    .then(({data}) => {

                        const projectData = {
                            id: projectId,
                            data: data.data,
                            expires: moment().add(15, 'minutes'),
                        };

                        commit('setProjectGeoStats', projectData);
                        commit('setProjectGeoStatsLoading', false);
                        resolve(data);
                    })
                    .catch(error => {
                        commit('setProjectGeoStatsLoading', false);
                        reject(error);
                    });
            } else {
                resolve(found);
            }
        });
    },

    /**
     * Get project supplier statistics for the project dash board
     */
    getProjectSupplierStats: ({state, getters, commit, dispatch}, projectId: number) => {

        return new Promise((resolve, reject) => {

            commit('setProjectSupplierStatsLoading', true);

            axios.get('/project/' + projectId + '/suppliers')

                .then(({data}) => {

                    const projectData = {
                        id: projectId,
                        data: data.data,
                    };

                    commit('setProjectSupplierStats', projectData);
                    commit('setProjectSupplierStatsLoading', false);
                    resolve(data);
                })
                .catch(error => {
                    commit('setProjectSupplierStatsLoading', false);
                    reject(error);
                });
        });
    },

    getProjectSupplierDownload: ({state, getters, commit, dispatch}, {projectId, supplierId}) => {

        return new Promise((resolve, reject) => {

            axios.get('/project/' + projectId + '/suppliers/' + supplierId + '/download')

                .then(({data}) => {
                    resolve(data);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    getSupplierTestLink: ({state, getters, commit, dispatch}, {projectId, supplierId}) => {

        return new Promise((resolve, reject) => {

            axios.get('/project/' + projectId + '/suppliers/' + supplierId + '/test-link')

                .then((response) => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    getEntryTestLink: ({state, getters, commit, dispatch}, {projectId, subPanelId, locale}) => {

        return new Promise((resolve, reject) => {

            axios.get('/project/' + projectId + '/entry-test-link/' + subPanelId + '/' + locale)

                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    sendTestEmail: ({state, getters, commit, dispatch}, data) => {

        return new Promise((resolve, reject) => {

            axios.post('/test-email', data)

                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    deleteProjectErrors: ({state, getters, commit, dispatch}, projectId: number) => {

        return new Promise((resolve, reject) => {

            commit('deleteProjectErrors', projectId);

            axios.delete('/project/' + projectId + '/errors')

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });
        });
    },

    deleteProjectError: ({state, getters, commit, dispatch}, error: ProjectErrorInterface) => {

        return new Promise((resolve, reject) => {

            commit('deleteProjectError', error);

            axios.delete('/project/error/' + error.id)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {

                    reject(error);
                });
        });
    },

    loadParticipants: ({state, getters, commit, dispatch}, {projectId, cursor, filter}) => {

        return new Promise((resolve, reject) => {

            let url = '/project/' + projectId + '/participants';

            let params = [];

            if (cursor) {
                params.push('cursor=' + cursor);
            }

            if (filter) {
                params.push('filter=' + filter);
            }

            if (params.length > 0) {
                url += "?" + params.join('&');
            }

            axios.get(url)

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    disqualifyParticipants: ({state, getters, commit, dispatch}, {projectId, reason, panelistIds}) => {

        return new Promise((resolve, reject) => {

            axios.post('/project/' + projectId + '/participants/' + reason, {identifiers: panelistIds})

                .then((response: AxiosResponse) => {
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    loadQuotas: ({state, getters, commit, dispatch}, id) => {

        return new Promise((resolve, reject) => {

            axios.get('/project/' + id + '/quotas')

                .then((response) => {

                    resolve(response);

                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    loadFeasibility: ({state, getters, commit, dispatch}, data) => {

        return new Promise((resolve, reject) => {

            axios.post('/feasibility', data)

                .then((response) => {

                    resolve(response);

                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    toggleEnforceQuota: ({state, getters, commit, dispatch}, {enforce, projectId, quotaId}) => {

        return new Promise((resolve, reject) => {

            axios.post('/project/' + projectId + '/quota/' + quotaId, {enforce: enforce})

                .then((response) => {

                    resolve(response);

                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    exportParticipants: ({state, getters, commit, dispatch}, {status, projectId}) => {
        return new Promise((resolve, reject) => {

            axios.post('/project/' + projectId + '/export-participants', {status: status})

                .then((response) => {

                    resolve(response);

                })
                .catch(error => {
                    reject(error);
                });
        });
    }
}
